import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '~ai-tutor/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-brand-hover disabled:bg-disabled disabled:text-disabled rounded-full',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-accent-subtle text-secondary-foreground hover:bg-[var(--ld-color-content-accent-knockout)] rounded-full disabled:bg-disabled disabled:text-disabled',
        tertiary: cn([
          // default
          'border',
          'bg-button-tertiary-default',
          'text-button-tertiary-default',
          'border-button-tertiary-default',
          'outline-button-tertiary-default',
          // hover
          'hover:bg-button-tertiary-hover',
          'hover:text-button-tertiary-hover',
          'hover:border-button-tertiary-hover',
          'hover:outline-button-tertiary-hover',
          // focus
          'focus-visible:bg-button-tertiary-focus',
          'focus-visible:text-button-tertiary-focus',
          'focus-visible:border-button-tertiary-focus',
          'focus-visible:outline-button-tertiary-focus',
          // disabled
          'disabled:bg-button-tertiary-disabled',
          'disabled:text-button-tertiary-disabled',
          'disabled:border-button-tertiary-disabled',
          'disabled:outline-button-tertiary-disabled',
          'rounded-full',
        ]),

        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 [&_svg]:mr-2 disabled:bg-transparent disabled:text-disabled',
        link: 'text-primary underline-offset-4 hover:underline',
        icon: 'bg-transparent hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 rounded-full shadow-md',
        cta: 'bg-background rounded-full shadow-[0_0_25px_rgba(111,1,245,0.3)] hover:shadow-[0_0_25px_rgba(111,1,245,0.5)] transition-shadow [&_svg]:text-primary gap-3',
      },
      size: {
        default: 'px-12 py-8 has-[>svg]:px-3',
        sm: 'h-8 rounded-full gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'rounded-full px-16 py-8 has-[>svg]:px-16',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
        'icon-xl': 'size-14',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
